import pako from "pako";

/**
 * Shared decoder for the question-bank payloads returned by
 * `/v1/content/all-questions` and `/v1/content/deleted-questions`.
 *
 * Both endpoints return the same envelope, but the two call sites used to
 * decode it with separate copy-pasted implementations. They drifted: the
 * deleted-questions copy dereferenced `questions_compressed`**c** (a typo) and
 * supported only one of the four wire shapes, so the deleted-question map was
 * never populated. Any past exam containing a retired question then failed
 * `Test.allQuestionsLoaded()` and vanished from the results and history
 * screens.
 *
 * Keeping one implementation is the actual fix — correcting the typo alone
 * would have left the deleted path unable to handle the non-Buffer shapes.
 *
 * The payload may arrive as any of:
 *  - a serialised Node Buffer: `{ type: "Buffer", data: number[] }`
 *  - an object exposing `toUint8Array()`
 *  - an object exposing a `_binaryString`
 *  - a base64 string
 *
 * Returns the decoded question map. If the payload is not compressed it is
 * returned unchanged. If decompression fails the error is surfaced to the
 * caller so it can decide whether to preserve last-known-good data, rather
 * than silently yielding an empty map.
 */
export function inflateQuestionsPayload(data: any): any {
  if (!data?.questions_compressed) return data;

  const compressed = data.questions_compressed;
  let compressedBytes: Uint8Array;

  if (compressed.type === "Buffer" && Array.isArray(compressed.data)) {
    compressedBytes = Uint8Array.from(compressed.data as number[]);
  } else if (typeof compressed.toUint8Array === "function") {
    compressedBytes = compressed.toUint8Array();
  } else if (typeof compressed._binaryString === "string") {
    compressedBytes = Uint8Array.from(
      compressed._binaryString.split("").map((c: string) => c.charCodeAt(0)),
    );
  } else if (typeof compressed === "string") {
    const raw = atob(compressed);
    compressedBytes = Uint8Array.from(
      Array.from(raw).map((c) => c.charCodeAt(0)),
    );
  } else {
    throw new Error(
      `Unsupported compressed questions payload: ${Object.prototype.toString.call(
        compressed,
      )}`,
    );
  }

  const jsonString = pako.inflate(compressedBytes, { to: "string" });
  return JSON.parse(jsonString);
}
